import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * Delete Account button — required by Google Play Store (in-app data deletion).
 * Shows a confirmation dialog asking the user to type "SUPPRIMER" before wiping
 * the account. On success, logs the user out and redirects to onboarding.
 */
export default function DeleteAccountButton({ testId = "delete-account-btn" }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = confirmText.trim().toUpperCase() === "SUPPRIMER";

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/me");
      toast.success("Compte supprimé. À bientôt !");
      logout?.();
      nav("/onboarding", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible pour le moment.");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-8 mx-5 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-red-800 text-sm">Zone dangereuse</div>
            <p className="text-xs text-red-700/80 mt-1 leading-relaxed">
              La suppression de votre compte est <strong>immédiate et définitive</strong>. Toutes vos
              données personnelles (profil, demandes, messages, avis, jetons restants) sont effacées.
              Cette action est irréversible.
            </p>
            <Button
              variant="outline"
              onClick={() => setOpen(true)}
              className="mt-3 border-red-300 text-red-700 hover:bg-red-100 rounded-full h-10"
              data-testid={testId}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer mon compte
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Supprimer définitivement le compte
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de supprimer <strong>{user?.email}</strong> et toutes les données
              associées. Cette action ne peut pas être annulée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-ink">
              Pour confirmer, tapez <span className="font-mono font-bold">SUPPRIMER</span> ci-dessous&nbsp;:
            </p>
            <Input
              autoFocus
              placeholder="SUPPRIMER"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button
              onClick={doDelete}
              disabled={!canDelete || busy}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid="delete-confirm-btn"
            >
              {busy ? "Suppression…" : "Supprimer définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
